import { Link } from 'react-router-dom'
import type { AssignmentWithDetails } from '../../services'
import type { AssignmentStatus } from '../../types/database.types'

interface AssignmentCardProps {
  assignment: AssignmentWithDetails
  onCancel?: () => void
  onComplete?: () => void
}

const statusColors: Record<AssignmentStatus, { bg: string; text: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-800' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-800' },
  expired: { bg: 'bg-red-100', text: 'text-red-800' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-800' },
}

export function AssignmentCard({
  assignment,
  onCancel,
  onComplete,
}: AssignmentCardProps) {
  const statusColor = statusColors[assignment.status]
  const progress = assignment.progress_summary

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Link
            to={`/dashboard/assignments/${assignment.id}`}
            className="font-semibold text-gray-900 hover:text-blue-600"
          >
            {assignment.program?.title || 'Untitled Program'}
          </Link>
          <p className="text-sm text-gray-500 mt-1">
            Assigned to {assignment.client?.full_name || 'Unknown Client'}
          </p>
        </div>
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor.bg} ${statusColor.text}`}
        >
          {assignment.status}
        </span>
      </div>

      {/* Progress bar */}
      {progress && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium text-gray-900">
              {progress.completed_items}/{progress.total_items} completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.progress_percent}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
        <span>
          Assigned {new Date(assignment.assigned_at).toLocaleDateString()}
        </span>
        {assignment.expires_at && (
          <>
            <span>·</span>
            <span>
              Expires {new Date(assignment.expires_at).toLocaleDateString()}
            </span>
          </>
        )}
      </div>

      {assignment.notes && (
        <p className="mt-3 text-sm text-gray-600 line-clamp-2">
          {assignment.notes}
        </p>
      )}

      {assignment.status === 'active' && (
        <div className="mt-4 flex items-center justify-end gap-3">
          {onComplete && (
            <button
              onClick={onComplete}
              className="text-sm text-green-600 hover:text-green-800"
            >
              Mark Complete
            </button>
          )}
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  )
}
